import { LoanStep } from './loan-palier.model';
import {
  LoanPalierLike,
  palierBoundaryMonths,
  palierRanges,
} from './loan-palier.utils';

export const LOAN_AMORTIZATION_PAGE_SIZE = 12;
export const LOAN_AMORTIZATION_CARD_MODE_MIN_WIDTH = 992;
export const LOAN_AMORTIZATION_BOUNDARY_CLASS = 'ea-loan-amortization-boundary';
export const LOAN_AMORTIZATION_DIFFERE_CLASS = 'ea-loan-amortization-differe';
export const LOAN_AMORTIZATION_TOTAL_CLASS = 'ea-loan-amortization-total';
export const LOAN_AMORTIZATION_TOTAL_LABEL = 'Total du crédit';

const MONTHS_PER_YEAR = 12;

export interface LoanAmortizationRow {
  echeance: number | string;
  palier: string;
  capital: number;
  interest: number;
  insurance: number;
  amountMonthly: number;
  restant: number | null;
  rowClass: string;
}

export interface LoanAmortizationTotals {
  capital: number;
  interest: number;
  insurance: number;
  amountMonthly: number;
}

export function filterAmortizationSteps(
  steps: LoanStep[] | null | undefined,
): LoanStep[] {
  if (!Array.isArray(steps)) {
    return [];
  }

  return steps.filter(
    step => (step?.monthNumber ?? 0) > 0 && step?.period !== 'AFTER',
  );
}

export function hasDiffereStep(steps: LoanStep[] | null | undefined): boolean {
  return filterAmortizationSteps(steps).some(step => step.period === 'DIFFERE');
}

export function buildMonthlyAmortizationRows(
  steps: LoanStep[] | null | undefined,
  paliers: LoanPalierLike[] | null | undefined,
): LoanAmortizationRow[] {
  const ranges = palierRanges(paliers);
  const boundaries = palierBoundaryMonths(paliers);

  return filterAmortizationSteps(steps).map(step => {
    const month = step.monthNumber ?? 0;
    const isDiffere = step.period === 'DIFFERE';
    const isBoundary = boundaries.has(month);
    const range = ranges.find(
      item => month >= item.startMonth && month <= item.endMonth,
    );
    const palierLabel = range ? String(range.index + 1) : '';

    return {
      echeance: month,
      palier: palierLabel + (isDiffere ? ' · différé' : ''),
      capital: step.loanMonth ?? 0,
      interest: step.interestMonth ?? 0,
      insurance: step.insuranceMonth ?? 0,
      amountMonthly: step.totalMonth ?? 0,
      restant: step.loanLeft ?? 0,
      rowClass: isBoundary
        ? LOAN_AMORTIZATION_BOUNDARY_CLASS
        : isDiffere
          ? LOAN_AMORTIZATION_DIFFERE_CLASS
          : '',
    };
  });
}

export function buildYearlyAmortizationRows(
  steps: LoanStep[] | null | undefined,
): LoanAmortizationRow[] {
  const buckets = new Map<number, LoanAmortizationRow>();
  const lastMonths = new Map<number, number>();

  filterAmortizationSteps(steps).forEach(step => {
    const month = step.monthNumber ?? 0;
    const year = Math.ceil(month / MONTHS_PER_YEAR);
    const bucket = buckets.get(year);

    if (!bucket) {
      buckets.set(year, {
        echeance: year,
        palier: '',
        capital: step.loanMonth ?? 0,
        interest: step.interestMonth ?? 0,
        insurance: step.insuranceMonth ?? 0,
        amountMonthly: step.totalMonth ?? 0,
        restant: step.loanLeft ?? 0,
        rowClass: '',
      });
      lastMonths.set(year, month);

      return;
    }

    bucket.capital += step.loanMonth ?? 0;
    bucket.interest += step.interestMonth ?? 0;
    bucket.insurance += step.insuranceMonth ?? 0;
    bucket.amountMonthly += step.totalMonth ?? 0;

    if (month >= (lastMonths.get(year) ?? 0)) {
      lastMonths.set(year, month);
      bucket.restant = step.loanLeft ?? 0;
    }
  });

  return Array.from(buckets.values()).sort(
    (a, b) => Number(a.echeance) - Number(b.echeance),
  );
}

export function buildTotalAmortizationRow(
  totals: LoanAmortizationTotals,
): LoanAmortizationRow {
  return {
    echeance: LOAN_AMORTIZATION_TOTAL_LABEL,
    palier: '',
    capital: totals.capital,
    interest: totals.interest,
    insurance: totals.insurance,
    amountMonthly: totals.amountMonthly,
    restant: null,
    rowClass: LOAN_AMORTIZATION_TOTAL_CLASS,
  };
}

export function sumAmortizationRows(
  rows: LoanAmortizationRow[] | null | undefined,
): LoanAmortizationTotals {
  const totals: LoanAmortizationTotals = {
    capital: 0,
    interest: 0,
    insurance: 0,
    amountMonthly: 0,
  };

  if (!Array.isArray(rows)) {
    return totals;
  }

  rows.forEach(row => {
    totals.capital += row.capital;
    totals.interest += row.interest;
    totals.insurance += row.insurance;
    totals.amountMonthly += row.amountMonthly;
  });

  return totals;
}
